import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { Label } from '@reflex/ui/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@reflex/ui/components/ui/tabs';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { CommandBlock } from '@reflex/ui/components/content/CommandBlock';
import {
  CodexSubscriptionCredentialSchema,
  type CodexSubscriptionCredential,
} from '@reflex/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';

const CODEX_PLATFORM_COMMANDS = {
  mac: `cat ~/.codex/auth.json | pbcopy`,
  linux: `cat ~/.codex/auth.json`,
  windows: `Get-Content $HOME\\.codex\\auth.json`,
} as const;
type CodexPlatform = keyof typeof CODEX_PLATFORM_COMMANDS;

const credentialDialogContentClass =
  'flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-[500px]';
const credentialDialogBodyClass = 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4 pr-1';
const credentialTextareaClass =
  'max-h-[40vh] min-h-[180px] w-full min-w-0 max-w-full resize-y overflow-auto font-mono text-sm';
/** High-visibility caution strip under the paste step (Alert has no warning variant). */
const credentialInfoAlertClass =
  'border-2 border-status-warning bg-status-warning/40 text-foreground [&>svg]:text-status-warning *:data-[slot=alert-description]:text-foreground';
const credentialInfoCodeClass = 'rounded bg-status-warning/50 px-1 py-0.5 font-mono';

export interface RegisterCodexDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fired after a successful registration, with the `mps_*` id of the
   * subscription that was written so callers can act on it (select it in a
   * form, re-point a running agent at it). Null when the server could not
   * mirror the credential into a selectable row, which means "registered, but
   * pick it from the list yourself".
   */
  onRegistered?: (modelProviderSecretId: string | null) => void;
}

/**
 * Paste-JSON registration flow for a personal Codex subscription
 * credential. Mirrors {@link RegisterClaudeMaxDialog} — self-contained
 * dialog with its own state, validation, and mutation. Subscriptions are
 * user-tier only.
 */
export function RegisterCodexDialog({
  open,
  onOpenChange,
  onRegistered,
}: RegisterCodexDialogProps) {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const [jsonInput, setJsonInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<CodexPlatform>('mac');

  const registerMutation = useMutation({
    mutationFn: (blob: CodexSubscriptionCredential) => api.registerCodexSubscription(blob),
    onSuccess: async (registered) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.userCredentials(orgKey) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.codexSubscriptionStatus(orgKey),
      });
      setJsonInput('');
      setParseError(null);
      onOpenChange(false);
      toast.success('Codex subscription credential registered');
      // Await the accessible-secrets refresh before handing the new id to the
      // caller: a picker reconciling against the pre-registration list would
      // otherwise treat that id as invalid and overwrite the caller's selection.
      await queryClient.invalidateQueries({
        queryKey: ['user', 'model-provider-secrets', 'accessible', orgKey],
      });
      onRegistered?.(registered.modelProviderSecretId);
    },
    onError: (error: Error) =>
      toast.error('Failed to register credential', { description: error.message }),
  });

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setJsonInput('');
      setParseError(null);
    }
  }

  function handleRegister() {
    setParseError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch {
      setParseError('Invalid JSON. Please paste the contents of your auth.json file.');
      return;
    }
    const result = CodexSubscriptionCredentialSchema.safeParse(parsed);
    if (!result.success) {
      const issues = result.error.issues.map((i) => i.message).join(', ');
      setParseError(`Invalid credential format: ${issues}`);
      return;
    }
    registerMutation.mutate(result.data);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={credentialDialogContentClass}>
        <DialogHeader>
          <DialogTitle>Register Codex Credential</DialogTitle>
          <DialogDescription>
            Run the command below to copy your Codex credential, then paste it here.
          </DialogDescription>
        </DialogHeader>
        <div className={credentialDialogBodyClass}>
          <div className="space-y-2">
            <div className="flex w-full items-center justify-between">
              <Label>1. Get your credential</Label>
              <Tabs value={platform} onValueChange={(v) => setPlatform(v as CodexPlatform)}>
                <TabsList className="h-7">
                  <TabsTrigger value="mac" className="px-2.5 text-xs h-5">
                    macOS
                  </TabsTrigger>
                  <TabsTrigger value="linux" className="px-2.5 text-xs h-5">
                    Linux
                  </TabsTrigger>
                  <TabsTrigger value="windows" className="px-2.5 text-xs h-5">
                    Windows
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <CommandBlock commands={CODEX_PLATFORM_COMMANDS[platform]} />
            <p className="text-xs text-muted-foreground">
              {platform === 'mac'
                ? 'Copies your Codex credential from the local auth file to your clipboard.'
                : platform === 'windows'
                  ? 'Prints your Codex credential from the local auth file. Run in PowerShell.'
                  : 'Prints your Codex credential from the local auth file.'}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="codex-credential-json">2. Paste output here</Label>
            <Textarea
              id="codex-credential-json"
              placeholder='{"auth_mode": "chatgpt", ...}'
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              className={credentialTextareaClass}
            />
            {parseError && <p className="text-sm text-destructive">{parseError}</p>}
          </div>
          <Alert variant="default" className={credentialInfoAlertClass}>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {/* Single <p> keeps <code> inline; AlertDescription's grid otherwise stacks children. */}
              <p>
                Do not run <code className={credentialInfoCodeClass}>codex login</code> again after
                you paste. Re-authenticating with Codex replaces your credential and invalidates the
                one you just gave Reflex, so the connection would stop working. Leave your existing
                Codex login in place.
              </p>
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button
            onClick={handleRegister}
            disabled={registerMutation.isPending || !jsonInput.trim()}
          >
            {registerMutation.isPending ? 'Registering...' : 'Register'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
